import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useTheme } from "@repo/tokens";
import { Spinner } from "../spinner";
import type { RippleButtonState, RippleButtonWebProps } from "./types";
import {
  SHADOW_TOKENS,
  SIZE_TOKENS,
  darken,
  resolveColors,
  resolveRadius,
} from "./tokens";

const KEYFRAME_ID = "ripple-button-keyframes";
const KEYFRAMES = `
@keyframes rippleBtn-ripple-enter {
  0%   { transform: scale(0);   opacity: 0; }
  12%  { opacity: 0.16; }
  100% { transform: scale(1);   opacity: 0.16; }
}
@keyframes rippleBtn-ripple-exit {
  from { opacity: 0.16; }
  to   { opacity: 0; }
}
`;

function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAME_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAME_ID;
  style.appendChild(document.createTextNode(KEYFRAMES));
  document.head.appendChild(style);
}

type RippleInstance = {
  id: number;
  x: number;
  y: number;
  size: number;
  exiting: boolean;
  startedAt: number;
};

const RIPPLE_ENTER_MS = 650;
const RIPPLE_EXIT_MS = 400;
// Floor on visible ripple time — without this a quick down+up (<50ms) would
// barely render any ink before the exit fade starts.
const RIPPLE_MIN_VISIBLE_MS = 280;

export const RippleButton = forwardRef<HTMLElement, RippleButtonWebProps>(
  (props, ref) => {
    const {
      children,
      variant = "primary",
      size = "sm",
      shape = "round",
      color = "primary",
      disabled = false,
      loading = false,
      loadingLabel,
      fullWidth = false,
      iconOnly = false,
      height: heightProp,
      width: widthProp,
      paddingX: paddingXProp,
      startIcon,
      endIcon,
      onPress,
      backgroundColor,
      borderColor,
      labelColor,
      fontSize,
      fontWeight,
      disableRipple = false,
      disableFocusRing = false,
      href,
      as,
      type = "button",
      className,
      style,
      onClick,
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      onPointerEnter,
      onKeyDown,
      onKeyUp,
      onFocus,
      onBlur,
      ...rest
    } = props;

    const { theme } = useTheme();
    const isInteractiveBlocked = disabled || loading;

    const Component: ElementType = as ?? (href ? "a" : "button");
    const isAnchor = Component === "a";
    const reactId = useId();

    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);
    const [focusVisible, setFocusVisible] = useState(false);
    const [ripples, setRipples] = useState<RippleInstance[]>([]);
    const rippleSeed = useRef(0);
    const rootRef = useRef<HTMLElement | null>(null);
    const keyboardRippleId = useRef<number | null>(null);

    useEffect(() => {
      ensureKeyframes();
    }, []);

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        rootRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref && "current" in ref)
          (ref as { current: HTMLElement | null }).current = node;
      },
      [ref],
    );

    const interactionState: RippleButtonState = isInteractiveBlocked
      ? "disabled"
      : pressed
        ? "pressed"
        : focusVisible
          ? "focused"
          : hovered
            ? "hovered"
            : "normal";

    const sizeTokens = SIZE_TOKENS[size];
    const colors = useMemo(
      () =>
        resolveColors({
          theme,
          variant,
          color,
          state: interactionState,
          iconOnly,
        }),
      [theme, variant, color, interactionState, iconOnly],
    );

    const baseHeight =
      iconOnly && sizeTokens.iconOnlyHeight !== undefined
        ? sizeTokens.iconOnlyHeight
        : sizeTokens.height;
    const iconRenderSize =
      iconOnly && sizeTokens.iconOnlyIconSize !== undefined
        ? sizeTokens.iconOnlyIconSize
        : sizeTokens.iconSize;
    const containerHeight = heightProp ?? baseHeight;
    const containerRadius = resolveRadius(shape, size);
    // Width priority: explicit > fullWidth > iconOnly square > intrinsic.
    const containerWidth: number | string | undefined =
      widthProp ??
      (fullWidth ? "100%" : iconOnly ? containerHeight : undefined);

    const containerBg = backgroundColor
      ? interactionState === "hovered" || interactionState === "focused"
        ? darken(backgroundColor, 0.1)
        : interactionState === "pressed"
          ? darken(backgroundColor, 0.18)
          : backgroundColor
      : colors.background;
    const resolvedLabelColor = labelColor ?? colors.label;

    const elevationShadow = SHADOW_TOKENS[colors.shadow].web;

    const strokeColor =
      borderColor ?? (variant === "outline" ? colors.border : null);
    const insetStroke = strokeColor ? `inset 0 0 0 1px ${strokeColor}` : null;
    const composedBoxShadow =
      [insetStroke, elevationShadow === "none" ? null : elevationShadow]
        .filter(Boolean)
        .join(", ") || "none";

    const spawnRipple = useCallback(
      (x: number, y: number) => {
        if (disableRipple || isInteractiveBlocked) return -1;
        const node = rootRef.current;
        if (!node) return -1;
        const rect = node.getBoundingClientRect();
        const localX = x - rect.left;
        const localY = y - rect.top;
        const dx = Math.max(localX, rect.width - localX);
        const dy = Math.max(localY, rect.height - localY);
        const radius = Math.sqrt(dx * dx + dy * dy);
        const id = ++rippleSeed.current;
        setRipples((prev) => [
          ...prev,
          {
            id,
            x: localX,
            y: localY,
            size: radius * 2,
            exiting: false,
            startedAt: performance.now(),
          },
        ]);
        return id;
      },
      [disableRipple, isInteractiveBlocked],
    );

    const startExit = useCallback((id: number) => {
      setRipples((prev) =>
        prev.map((r) => (r.id === id ? { ...r, exiting: true } : r)),
      );
      window.setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== id));
      }, RIPPLE_EXIT_MS);
    }, []);

    const releaseRipple = useCallback(
      (id: number) => {
        if (id < 0) return;
        const ripple = ripples.find((r) => r.id === id);
        if (!ripple) return;
        const elapsed = performance.now() - ripple.startedAt;
        const wait = Math.max(0, RIPPLE_MIN_VISIBLE_MS - elapsed);
        if (wait === 0) startExit(id);
        else window.setTimeout(() => startExit(id), wait);
      },
      [ripples, startExit],
    );

    const releaseAllRipples = useCallback(() => {
      const now = performance.now();
      ripples.forEach((r) => {
        const elapsed = now - r.startedAt;
        const wait = Math.max(0, RIPPLE_MIN_VISIBLE_MS - elapsed);
        if (wait === 0) startExit(r.id);
        else window.setTimeout(() => startExit(r.id), wait);
      });
    }, [ripples, startExit]);

    const lastRippleId = useRef<number>(-1);

    const handlePointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
      onPointerDown?.(e);
      if (isInteractiveBlocked) return;
      setPressed(true);
      lastRippleId.current = spawnRipple(e.clientX, e.clientY);
    };

    const handlePointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
      onPointerUp?.(e);
      if (!pressed) return;
      setPressed(false);
      releaseRipple(lastRippleId.current);
      lastRippleId.current = -1;
    };

    const handlePointerLeave = (e: ReactPointerEvent<HTMLButtonElement>) => {
      onPointerLeave?.(e);
      setHovered(false);
      if (pressed) {
        setPressed(false);
        releaseRipple(lastRippleId.current);
        lastRippleId.current = -1;
      }
    };

    const handlePointerEnter = (e: ReactPointerEvent<HTMLButtonElement>) => {
      onPointerEnter?.(e);
      if (isInteractiveBlocked) return;
      setHovered(true);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(e);
      if (isInteractiveBlocked) return;
      if (e.repeat) return;
      if (e.key === " " || e.key === "Enter") {
        const node = rootRef.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        keyboardRippleId.current = spawnRipple(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2,
        );
        setPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
      onKeyUp?.(e);
      if (e.key === " " || e.key === "Enter") {
        setPressed(false);
        if (keyboardRippleId.current != null) {
          releaseRipple(keyboardRippleId.current);
          keyboardRippleId.current = null;
        }
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
      onFocus?.(e);
      try {
        if (e.currentTarget.matches(":focus-visible")) {
          setFocusVisible(true);
        }
      } catch {
        setFocusVisible(true);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
      onBlur?.(e);
      setFocusVisible(false);
      if (pressed) {
        setPressed(false);
        releaseAllRipples();
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isInteractiveBlocked) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
      onPress?.();
    };

    const containerStyle: CSSProperties = {
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "row",
      gap: sizeTokens.gap,
      boxSizing: "border-box",
      height: containerHeight,
      width: containerWidth,
      minWidth: iconOnly ? containerHeight : undefined,
      paddingInline: iconOnly ? 0 : (paddingXProp ?? sizeTokens.paddingX),
      borderRadius: containerRadius,
      background: containerBg,
      color: resolvedLabelColor,
      border: "none",
      cursor: isInteractiveBlocked ? "not-allowed" : "pointer",
      userSelect: "none",
      WebkitTapHighlightColor: "transparent",
      textDecoration: "none",
      outline: "none",
      boxShadow: composedBoxShadow,
      transition:
        "box-shadow 180ms cubic-bezier(0.2, 0, 0, 1), background-color 160ms cubic-bezier(0.2, 0, 0, 1), border-color 160ms cubic-bezier(0.2, 0, 0, 1), color 160ms cubic-bezier(0.2, 0, 0, 1)",
      fontFamily:
        "Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: fontSize ?? sizeTokens.fontSize,
      lineHeight: `${sizeTokens.lineHeight}px`,
      fontWeight: fontWeight ?? sizeTokens.fontWeight,
      letterSpacing: sizeTokens.letterSpacing,
      overflow: "hidden",
      ...style,
    };

    // Keyboard focus ring — applied to all variants so Tab users always see
    // where they are; separate from the per-state background.
    const focusRingStyle: CSSProperties | undefined =
      focusVisible && !disableFocusRing && !isInteractiveBlocked
        ? {
            position: "absolute",
            inset: -4,
            borderRadius: containerRadius + 4,
            boxShadow: `0 0 0 3px ${theme.colors.primary[50]}`,
            pointerEvents: "none",
          }
        : undefined;

    const rippleContainerStyle: CSSProperties = {
      position: "absolute",
      inset: 0,
      borderRadius: containerRadius,
      overflow: "hidden",
      pointerEvents: "none",
    };

    const elementProps: Record<string, unknown> = {
      ref: setRefs,
      className,
      style: containerStyle,
      onClick: handleClick,
      onPointerDown: handlePointerDown,
      onPointerUp: handlePointerUp,
      onPointerEnter: handlePointerEnter,
      onPointerLeave: handlePointerLeave,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      onFocus: handleFocus,
      onBlur: handleBlur,
      "aria-disabled": isInteractiveBlocked || undefined,
      "aria-busy": loading || undefined,
      ...rest,
    };

    if (isAnchor) {
      elementProps.href = isInteractiveBlocked ? undefined : href;
      elementProps.role = "button";
      elementProps.tabIndex = isInteractiveBlocked ? -1 : 0;
    } else if (Component === "button") {
      elementProps.type = type;
      elementProps.disabled = isInteractiveBlocked;
    }

    const showStartIcon = startIcon && !iconOnly && !loading;
    const showEndIcon = endIcon && !iconOnly && !loading;
    const showIconOnly = iconOnly && !loading;
    return (
      <Component {...elementProps}>
        {!disableRipple && (
          <span aria-hidden="true" style={rippleContainerStyle}>
            {ripples.map((r) => (
              <RippleSpan
                key={r.id}
                x={r.x}
                y={r.y}
                size={r.size}
                exiting={r.exiting}
                color={colors.ripple}
              />
            ))}
          </span>
        )}

        {loading ? (
          <LoadingContent
            size={iconRenderSize}
            color={resolvedLabelColor}
            label={loadingLabel}
            iconOnly={iconOnly}
            id={`${reactId}-spinner`}
          />
        ) : (
          <>
            {showStartIcon && (
              <IconSlot size={sizeTokens.iconSize}>{startIcon}</IconSlot>
            )}
            {showIconOnly && (
              <IconSlot size={iconRenderSize}>
                {startIcon ?? endIcon ?? children}
              </IconSlot>
            )}
            {!iconOnly && children !== undefined && (
              <span
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {children}
              </span>
            )}
            {showEndIcon && (
              <IconSlot size={sizeTokens.iconSize}>{endIcon}</IconSlot>
            )}
          </>
        )}

        {focusRingStyle && <span aria-hidden="true" style={focusRingStyle} />}
      </Component>
    );
  },
);

RippleButton.displayName = "RippleButton";

function RippleSpan({
  x,
  y,
  size,
  exiting,
  color,
}: {
  x: number;
  y: number;
  size: number;
  exiting: boolean;
  color: string;
}) {
  const style: CSSProperties = {
    position: "absolute",
    left: x - size / 2,
    top: y - size / 2,
    width: size,
    height: size,
    borderRadius: "50%",
    background: color,
    opacity: 0,
    pointerEvents: "none",
    animation: exiting
      ? `rippleBtn-ripple-exit ${RIPPLE_EXIT_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
      : `rippleBtn-ripple-enter ${RIPPLE_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
    transformOrigin: "center",
  };
  return <span style={style} />;
}

function IconSlot({ size, children }: { size: number; children: ReactNode }) {
  // Inject the size token onto Icon-like children; explicit child `size` wins.
  const sized = React.isValidElement(children)
    ? React.cloneElement(children, {
        size: (children.props as { size?: number }).size ?? size,
      } as Partial<{ size: number }>)
    : children;

  // Wrapper sizes to icon's intrinsic dimensions — fixing it to `size` would
  // shrink consumer-overridden larger icons via flex-shrink.
  return (
    <span
      aria-hidden="true"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {sized}
    </span>
  );
}

function LoadingContent({
  size,
  color,
  label,
  iconOnly,
  id,
}: {
  size: number;
  color: string;
  label?: string;
  iconOnly: boolean;
  id: string;
}) {
  const spinnerSize = size <= 16 ? "sm" : size <= 24 ? "md" : "lg";
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <Spinner
        size={spinnerSize}
        color={color}
        id={id}
        label={label ?? "Loading"}
      />
      {!iconOnly && label ? <span>{label}</span> : null}
    </span>
  );
}

export default RippleButton;
export type { RippleButtonWebProps as RippleButtonProps } from "./types";
export type {
  RippleButtonVariant,
  RippleButtonSize,
  RippleButtonShape,
  RippleButtonColor,
  RippleButtonState,
} from "./types";
